import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SHOP, dialLink, appointmentOnlyDays } from "../lib/shop";
import Reveal from "../components/Reveal";
import { Star, Scissors, Clock, MapPin, ArrowRight, ArrowUpRight, Phone } from "lucide-react";

const HERO = "/hero-poster.jpg";

export default function Home() {
  const { shop, settings } = useAuth();
  const s = { ...SHOP, ...(shop || {}) };
  const callLink = dialLink(s.phone);
  const mapEmbed = s.map_embed || SHOP.mapEmbed;
  const apptPolicy = s.appointment_policy || appointmentOnlyDays(settings?.working_hours) ||
    "Monday, Wednesday, Thursday & Saturday are appointment-only.";
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [reviews, setReviews] = useState([]);

  const videoRef = useRef(null);
  const [reducedMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, []);

  // Dedicated 9:16 mobile files are used automatically once added; otherwise the 16:9
  // source falls back and is centre-cropped via object-cover/object-center.
  const videoSources = isMobile
    ? [["/hero-mobile.webm", "video/webm"], ["/hero-mobile.mp4", "video/mp4"], ["/hero.webm", "video/webm"], ["/hero.mp4", "video/mp4"]]
    : [["/hero.webm", "video/webm"], ["/hero.mp4", "video/mp4"]];

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.slice(0, 4))).catch(() => {});
    api.get("/barbers").then((r) => setBarbers(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  // Pause the hero video when the tab is hidden, resume when visible
  useEffect(() => {
    if (reducedMotion) return;
    const onVis = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) {
        v.pause();
      } else {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [reducedMotion]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section id="home" className="relative flex items-center min-h-screen [min-height:100svh]">
        <div className="absolute inset-0">
          <img src={HERO} alt="Who's Next barber shop interior" className="absolute inset-0 w-full h-full object-cover object-center" />
          {!reducedMotion && (
            <video
              key={isMobile ? "mobile" : "desktop"}
              ref={videoRef}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${videoLoaded ? "opacity-100" : "opacity-0"}`}
              poster={HERO}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
              onLoadedData={() => setVideoLoaded(true)}
              onPlaying={() => setVideoLoaded(true)}
            >
              {videoSources.map(([src, type]) => <source key={src} src={src} type={type} />)}
            </video>
          )}
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/20" />
        </div>
        <div
          className="relative z-10 max-w-7xl mx-auto w-full"
          style={{
            paddingTop: "max(7rem, calc(env(safe-area-inset-top) + 6rem))",
            paddingBottom: "max(4rem, env(safe-area-inset-bottom))",
            paddingLeft: "max(1.25rem, env(safe-area-inset-left))",
            paddingRight: "max(1.25rem, env(safe-area-inset-right))",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl sm:pl-3 lg:pl-0"
          >
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
              <span className="text-white font-medium">{s.rating}</span>
              <span className="label-caps">Rated by Preston</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tighter text-white">
              Who's <span className="text-[#D4AF37]">Next</span>
            </h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg text-gray-300 max-w-xl leading-relaxed">
              Preston's premium barber shop. Master craftsmanship, timeless style and
              a seamless booking experience — no phone tag, no waiting.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className="group px-8 py-4 rounded-full bg-[#D4AF37] text-black font-medium flex items-center justify-center gap-2 hover:bg-[#F3E5AB] transition-colors duration-300"
              >
                Book Now
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <a
                href={callLink}
                data-testid="hero-call-btn"
                className="px-8 py-4 rounded-full border border-white/20 text-white hover:border-white/60 transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <div className="relative z-10 border-y border-white/10 bg-[#0A0A0A] py-6 overflow-hidden">
        <div className="flex gap-12 whitespace-nowrap animate-none justify-center flex-wrap px-5">
          {["Skin Fades", "Hot Towel Shaves", "Beard Sculpting", "Classic Cuts", "Kids Cuts"].map((t) => (
            <span key={t} className="font-display text-2xl text-gray-500 flex items-center gap-4">
              <Scissors className="w-4 h-4 text-[#D4AF37]" /> {t}
            </span>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <section id="services" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <Reveal>
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <div className="label-caps mb-3">The Menu</div>
              <h2 className="font-display text-5xl sm:text-6xl tracking-tight">Signature Services</h2>
            </div>
            <Link to="/services" className="text-[#D4AF37] flex items-center gap-2 hover:gap-3 transition-all duration-300">
              Full menu <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08}>
              <div className="group bg-[#141414] border border-white/5 rounded-2xl p-8 flex items-center justify-between hover:-translate-y-1 hover:border-[#D4AF37]/30 transition-all duration-300">
                <div>
                  <h3 className="font-display text-3xl">{s.name}</h3>
                  <p className="text-gray-400 mt-2 max-w-md">{s.description}</p>
                  <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
                    <Clock className="w-4 h-4" /> {s.duration} min
                  </div>
                </div>
                <div className="text-right shrink-0 ml-6">
                  <div className="font-display text-4xl text-[#D4AF37]">£{s.price}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BARBERS */}
      <section id="barbers" className="relative z-10 bg-[#0D0D0D] border-y border-white/10 py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="label-caps mb-3">The Craftsmen</div>
            <h2 className="font-display text-5xl sm:text-6xl tracking-tight mb-14">Meet the Barbers</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {barbers.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.1}>
                <div className="group relative overflow-hidden rounded-2xl">
                  {b.photo ? (
                    <img src={b.photo} alt={b.name} className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-96 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
                      <span className="font-display text-7xl text-[#D4AF37]/40">{(b.name || "?").charAt(0)}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <h3 className="font-display text-3xl">{b.name}</h3>
                    <p className="text-gray-300 text-sm mt-1 line-clamp-2">{b.specialties?.length ? b.specialties.slice(0, 2).join(" · ") : (b.bio || "")}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <Reveal>
          <div className="text-center mb-14">
            <div className="label-caps mb-3">Word on the street</div>
            <h2 className="font-display text-5xl sm:text-6xl tracking-tight">{SHOP.rating} out of 5</h2>
            <div className="flex justify-center gap-1 mt-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />)}
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.08}>
              <div className="bg-[#141414] border border-white/5 rounded-2xl p-8 h-full">
                <div className="flex gap-1 mb-4">
                  {[...Array(r.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />)}
                </div>
                <p className="text-gray-300 leading-relaxed italic">"{r.text}"</p>
                <div className="mt-6 label-caps">{r.author}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA / LOCATION */}
      <section id="contact" className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="label-caps mb-3">Find us</div>
            <h2 className="font-display text-5xl tracking-tight mb-6">Appointment only,<br />craftsmanship always.</h2>
            <p className="text-gray-400 leading-relaxed mb-8 max-w-md">
              {apptPolicy} Reserve your chair in seconds and skip the queue entirely.
            </p>
            <div className="flex items-start gap-3 text-gray-300 mb-8">
              <MapPin className="w-5 h-5 text-[#D4AF37] mt-1" />
              <span>{s.address}</span>
            </div>
            <Link to="/book" className="group px-8 py-4 rounded-full bg-[#D4AF37] text-black font-medium inline-flex items-center gap-2 hover:bg-[#F3E5AB] transition-colors duration-300">
              Reserve Your Chair
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl overflow-hidden border border-white/10 h-80">
              <iframe title="map" src={mapEmbed} className="w-full h-full" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
